import { Badge, type BadgeProps, IconButton, useTheme } from "@mui/material";
import { BellIcon, MessageCircle } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useNotificationExcludedTypes } from "web_myplann/Api/Notifications/useNotificationExcludedTypes";
import { useGetNotificationsGroupedCountQuery } from "web_myplann/Api/Notifications/useNotificationGroupedCountQuery";
import { useNotificationCenterRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { useIsDashboardV2 } from "web_myplann/Dashboard/State/dashboardVariantContext";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { NOTIFICATION_CENTER_TABS } from "web_myplann/NotificationCenter/Constants/notificationCenterTabs";
import { device, NavigationTypes } from "web_myplann/Utils/device";

type Props = {
	variant: "gradient" | "flat";
	chatBadgeColor?: BadgeProps["color"];
};

export const DashboardHeaderActions: FC<Props> = ({
	variant,
	chatBadgeColor = "error",
}) => {
	const { palette } = useTheme();
	const navigate = useNavigate();
	const isDashboardV2 = useIsDashboardV2();
	const hasNotificationCenterRight = useNotificationCenterRightOptional();
	const { excludedTypes, isReady } = useNotificationExcludedTypes();
	const { data: notificationsCount } = useGetNotificationsGroupedCountQuery(
		true,
		excludedTypes,
		{ enabled: isReady },
	);

	const isGradient = variant === "gradient";

	const iconColor = isGradient
		? palette.darkmodeText.white
		: palette.deprecated.grey[700];

	const badgeVariant = isGradient ? "headerGradient" : "headerFlat";

	const bellColor = isGradient
		? palette.darkmodeText.white
		: palette.common.black;

	const handleChatClick = () => {
		if (isDashboardV2) {
			navigate(Routing.Chat.List.path());
		} else {
			device.navigationRequest(NavigationTypes.ChatScreen);
		}
		if (isGradient) device.changeDashboardColor("");
	};

	const handleNotificationsClick = () => {
		navigate(
			hasNotificationCenterRight
				? Routing.Notifications.path(NOTIFICATION_CENTER_TABS.Recent)
				: Routing.Notifications.path(),
		);
		if (isGradient) device.changeDashboardColor("");
	};

	return (
		<>
			<IconButton
				sx={isGradient ? undefined : { padding: 0 }}
				onClick={handleChatClick}
			>
				<Badge
					badgeContent={notificationsCount?.chatCount}
					color={chatBadgeColor}
					variant={badgeVariant}
				>
					<MessageCircle color={iconColor} />
				</Badge>
			</IconButton>
			<IconButton
				sx={isGradient ? undefined : { padding: 0 }}
				onClick={handleNotificationsClick}
			>
				<Badge
					badgeContent={notificationsCount?.count}
					color="error"
					variant={badgeVariant}
				>
					<BellIcon color={bellColor} />
				</Badge>
			</IconButton>
		</>
	);
};
